import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

interface ImportCardsDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (cards: string[][]) => void;
  sideCount: number;
  sideNames: string[];
}

interface ParsedRow {
  data: string[];
  hasError: boolean;
}

const ImportCardsDialog: React.FC<ImportCardsDialogProps> = ({
  open,
  onClose,
  onImport,
  sideCount,
  sideNames,
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ParsedRow[]>([]);
  const [delimiter, setDelimiter] = useState<string>('\t');
  const [displayDelimiter, setDisplayDelimiter] = useState<string>('\\t');

  // Преобразует разделитель для отображения (табуляция -> '\t')
  const formatDelimiterForDisplay = (delim: string): string => {
    if (delim === '\t') return '\\t';
    return delim;
  };

  // Преобразует отображаемый разделитель для использования ('\t' -> табуляция)
  const parseDelimiterFromDisplay = (display: string): string => {
    if (display === '\\t') return '\t';
    return display;
  };

  // Обновляем displayDelimiter при изменении delimiter
  useEffect(() => {
    setDisplayDelimiter(formatDelimiterForDisplay(delimiter));
  }, [delimiter]);

  const handleDelimiterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDisplayValue = e.target.value;
    setDisplayDelimiter(newDisplayValue);
    const newDelimiter = parseDelimiterFromDisplay(newDisplayValue);
    setDelimiter(newDelimiter);
    
    // Перепарсим текст с новым разделителем
    if (text) {
      parseText(text, newDelimiter);
    }
  };

  const parseText = (input: string, delim: string) => {
    try {
      // Фильтруем пустые строки
      const lines = input.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        setPreview([]);
        setError(null);
        return;
      }

      const parsedCards: ParsedRow[] = lines.map(line => {
        const sides = line.split(delim);
        let hasError = sides.length !== sideCount;
        
        // Адаптируем данные к нужному количеству столбцов
        let adjustedSides: string[];
        
        if (sides.length < sideCount) {
          // Дополняем недостающие стороны пустыми строками
          adjustedSides = [...sides, ...Array(sideCount - sides.length).fill('')];
        } else if (sides.length > sideCount) {
          // Берем только нужное количество сторон
          adjustedSides = sides.slice(0, sideCount);
        } else {
          adjustedSides = sides;
        }
        
        return { data: adjustedSides, hasError };
      });
      
      setPreview(parsedCards);
      
      // Показываем предупреждение, если есть строки с несоответствующим количеством столбцов
      const hasAnyErrors = parsedCards.some(card => card.hasError);
      if (hasAnyErrors) {
        setError(t('cards.importWarning'));
      } else {
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.invalidFormat'));
      setPreview([]);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setText(newText);
    
    if (!newText.trim()) {
      setPreview([]);
      setError(null);
      return;
    }
    
    // Фильтруем пустые строки для анализа разделителя
    const nonEmptyLines = newText.split('\n').filter(line => line.trim());
    if (nonEmptyLines.length === 0) {
      setPreview([]);
      setError(null);
      return;
    }
    
    // Автоматически определяем разделитель только если не введен пользовательский
    const firstLine = nonEmptyLines[0];
    const possibleDelimiters = ['\t', ',', ';', '|'];
    
    // Проверяем каждый разделитель и выбираем тот, который дает количество сторон, равное sideCount
    let newDelimiter = delimiter;
    
    for (const delim of possibleDelimiters) {
      if (firstLine.split(delim).length === sideCount) {
        newDelimiter = delim;
        setDelimiter(delim);
        setDisplayDelimiter(formatDelimiterForDisplay(delim));
        break;
      }
    }
    
    // Используем новый или текущий разделитель
    parseText(newText, newDelimiter);
  };

  const handleImport = () => {
    if (preview.length > 0) {
      // Преобразуем формат данных для импорта
      const cardsToImport = preview.map(row => row.data);
      onImport(cardsToImport);
      setText('');
      setPreview([]);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('cards.importCards')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            multiline
            rows={10}
            fullWidth
            value={text}
            onChange={handleTextChange}
            placeholder={t('cards.enterCardData')}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('cards.delimiter')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('cards.detectedDelimiter')}:
              </Typography>
              <TextField
                size="small"
                value={displayDelimiter}
                onChange={handleDelimiterChange}
                sx={{ width: '80px' }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('cards.delimiterHelp')}
            </Typography>
          </Box>

          {error && (
            <Alert severity={error === t('cards.importWarning') ? "warning" : "error"} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {preview.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {sideNames.map((name, i) => (
                      <TableCell key={i} align="left">
                        {name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.map((parsedRow, index) => (
                    <TableRow 
                      key={index} 
                      sx={parsedRow.hasError ? { backgroundColor: 'rgba(255,0,0,0.05)' } : {}}
                    >
                      {parsedRow.data.map((side, sideIndex) => (
                        <TableCell key={sideIndex} align="left">
                          {side}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button 
          onClick={handleImport} 
          variant="contained" 
          color="primary"
          disabled={preview.length === 0}
        >
          {t('cards.importCardsCount', { count: preview.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportCardsDialog; 